import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, Container, Grid, Stack, Typography, Box, Tabs, Tab, Chip } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import DashboardStatBox from './components/DashboardStatBox';
import FinanceDataFilter from './financeComponents/filter';
import { fCurrency } from '../../../utils/formatNumber';

const ensureArray = v => (Array.isArray(v) ? v : []);
const safeNumber = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const lineQuantity = v => {
  const quantity = safeNumber(v);
  return quantity > 0 ? quantity : 1;
};
const lineAmount = v => safeNumber(v);
const safeString = v => (v === null || v === undefined ? '-' : String(v));
const toDateStr = v => {
  if (!v && v !== 0) return '-';
  const n = Number(v);
  const d = Number.isFinite(n) && String(v).length >= 10 ? new Date(n) : new Date(v);
  if (isNaN(d.getTime())) return safeString(v).slice(0, 10);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const computeFinanceSummary = data => {
  const list = ensureArray(data);
  if (!list.length) return { guests: 0, items: 0, revenue: 0, cost: 0, profit: 0, payments: 0, outstanding: 0, byCategory: {}, paymentsByType: {} };
  let revenue = 0;
  let cost = 0;
  let items = 0;
  const byCategory = {};
  const paymentsByType = {};
  let paymentsTotal = 0;
  let outstanding = 0;
  list.forEach(g => {
    const bills = ensureArray(g?.billing);
    const pays = ensureArray(g?.payments);
    let guestBill = 0;
    bills.forEach(b => {
      const line = lineAmount(b?.amount);
      const lineCost = lineAmount(b?.cost);
      revenue += line;
      cost += lineCost;
      guestBill += line;
      items += lineQuantity(b?.qty);
      const cat = safeString(b?.category || 'Uncategorized');
      byCategory[cat] = safeNumber(byCategory[cat]) + line;
    });
    let guestPays = 0;
    pays.forEach(p => {
      const amt = safeNumber(p?.amount);
      paymentsTotal += amt;
      guestPays += amt;
      const t = safeString(p?.paymentType || 'Unknown');
      paymentsByType[t] = safeNumber(paymentsByType[t]) + amt;
    });
    const bal = guestBill - guestPays;
    outstanding += bal > 0 ? bal : 0;
  });
  const profit = revenue - cost;
  return { guests: list.length, items, revenue, cost, profit, payments: paymentsTotal, outstanding, byCategory, paymentsByType };
};

const FinanceDashboard = () => {
  const { themeStretch } = useSettings();
  const [data, setData] = useState([]);
  const [selectedGuestId, setSelectedGuestId] = useState(null);
  const [reportTab, setReportTab] = useState(0);

  const summary = useMemo(() => computeFinanceSummary(data), [data]);

  const perGuest = useMemo(() => {
    const list = ensureArray(data);
    return list.map((g, idx) => {
      const id = g?.id ?? `g-${idx}`;
      const name = safeString(g?.name || '-');
      const bills = ensureArray(g?.billing);
      const pays = ensureArray(g?.payments);
      let revenue = 0;
      let cost = 0;
      let items = 0;
      const byCategory = {};
      bills.forEach(b => {
        const qty = lineQuantity(b?.qty);
        const line = lineAmount(b?.amount);
        const lineCost = lineAmount(b?.cost);
        revenue += line;
        cost += lineCost;
        items += qty;
        const cat = safeString(b?.category || 'Uncategorized');
        byCategory[cat] = safeNumber(byCategory[cat]) + line;
      });
      let payments = 0;
      const paymentsByType = {};
      pays.forEach(p => {
        const amt = safeNumber(p?.amount);
        payments += amt;
        const t = safeString(p?.paymentType || 'Unknown');
        paymentsByType[t] = safeNumber(paymentsByType[t]) + amt;
      });
      const profit = revenue - cost;
      const balance = revenue - payments;
      const billing = bills.map((r, i) => ({ ...r, id: r?.id ?? `b-${id}-${i}` }));
      const paymentsList = pays.map((r, i) => ({ ...r, id: r?.id ?? `p-${id}-${i}` }));
      return {
        id,
        name,
        checkInDate: safeString(g?.checkInDate || '-'),
        checkOutDate: safeString(g?.checkOutDate || '-'),
        items,
        revenue,
        cost,
        profit,
        payments,
        balance,
        byCategory,
        paymentsByType,
        billing,
        paymentsList
      };
    });
  }, [data]);

  useEffect(() => {
    if (!perGuest.find(x => x.id === selectedGuestId)) setSelectedGuestId(null);
  }, [perGuest, selectedGuestId]);

  const guestColumns = [
    { field: 'name', headerName: 'Guest', flex: 1, minWidth: 160, valueGetter: p => safeString(p.row?.name) },
    { field: 'checkInDate', headerName: 'Check In', width: 120, valueFormatter: p => toDateStr(p.value) },
    { field: 'checkOutDate', headerName: 'Check Out', width: 120, valueFormatter: p => toDateStr(p.value) },
    { field: 'items', headerName: 'Items', type: 'number', width: 90, valueGetter: p => safeNumber(p.row?.items) },
    { field: 'revenue', headerName: 'Revenue', width: 130, valueFormatter: p => fCurrency(safeNumber(p.value)) },
    { field: 'profit', headerName: 'Profit', width: 130, valueFormatter: p => fCurrency(safeNumber(p.value)) },
    { field: 'payments', headerName: 'Payments', width: 130, valueFormatter: p => fCurrency(safeNumber(p.value)) },
    { field: 'balance', headerName: 'Balance', width: 130, valueFormatter: p => fCurrency(safeNumber(p.value)) }
  ];

  const toolbarProps = { slotProps: { toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } } };
  const selectedGuest = perGuest.find(x => x.id === selectedGuestId) || null;

  const categoryRows = useMemo(
    () => Object.entries(summary.byCategory || {}).map(([k, v], i) => ({ id: `c-${i}`, category: safeString(k), revenue: safeNumber(v) })),
    [summary]
  );

  const paymentTypeRows = useMemo(
    () => Object.entries(summary.paymentsByType || {}).map(([k, v], i) => ({ id: `pt-${i}`, type: safeString(k), amount: safeNumber(v) })),
    [summary]
  );

  const outstandingRows = useMemo(
    () => perGuest.filter(g => safeNumber(g.balance) > 0).map(g => ({ id: `o-${g.id}`, name: safeString(g.name), balance: safeNumber(g.balance), revenue: safeNumber(g.revenue), payments: safeNumber(g.payments) })),
    [perGuest]
  );

  const dailyRows = useMemo(() => {
    const map = {};
    ensureArray(data).forEach(g => {
      ensureArray(g?.billing).forEach(b => {
        const d = toDateStr(b?.datetime);
        const line = lineAmount(b?.amount);
        const cost = lineAmount(b?.cost);
        if (!map[d]) map[d] = { date: d, revenue: 0, cost: 0, profit: 0, items: 0 };
        map[d].revenue += line;
        map[d].cost += cost;
        map[d].profit = map[d].revenue - map[d].cost;
        map[d].items += lineQuantity(b?.qty);
      });
    });
    return Object.values(map)
      .sort((a, b) => (a.date > b.date ? -1 : 1))
      .map((r, i) => ({ id: `d-${i}`, ...r }));
  }, [data]);

  const productRows = useMemo(() => {
    const map = {};
    ensureArray(data).forEach(g => {
      ensureArray(g?.billing).forEach(b => {
        const key = safeString(b?.title || 'Untitled');
        const qty = lineQuantity(b?.qty);
        const line = lineAmount(b?.amount);
        const cost = lineAmount(b?.cost);
        if (!map[key]) map[key] = { title: key, revenue: 0, cost: 0, profit: 0, qty: 0, count: 0 };
        map[key].revenue += line;
        map[key].cost += cost;
        map[key].profit = map[key].revenue - map[key].cost;
        map[key].qty += qty;
        map[key].count += 1;
      });
    });
    return Object.values(map)
      .sort((a, b) => b.revenue - a.revenue)
      .map((r, i) => ({ id: `p-${i}`, ...r }));
  }, [data]);

  const safeTab = Number.isInteger(reportTab) && reportTab >= 0 && reportTab <= 4 ? reportTab : 0;

  return (
    <Page title="Finance Dashboard">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Grid container spacing={2}>
          <FinanceDataFilter data={data} setData={setData} />

          <Grid item xs={12} md={2.4}>
            <DashboardStatBox title="Guests" value={safeNumber(summary.guests)} icon={<Icon icon="mdi:account-group" width={28} />} />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <DashboardStatBox title="Items" value={safeNumber(summary.items)} icon={<Icon icon="mdi:cart-outline" width={28} />} />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <DashboardStatBox title="Revenue" value={fCurrency(safeNumber(summary.revenue))} icon={<Icon icon="mdi:cash-multiple" width={28} />} />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <DashboardStatBox title="Payments" value={fCurrency(safeNumber(summary.payments))} icon={<Icon icon="mdi:credit-card-outline" width={28} />} />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <DashboardStatBox title="Profit" value={fCurrency(safeNumber(summary.profit))} icon={<Icon icon="mdi:trending-up" width={28} />} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ overflow: 'auto' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1">Per Guest Summary</Typography>
                  {safeNumber(summary.outstanding) > 0 && <Chip size="small" color="warning" label={`Outstanding ${fCurrency(safeNumber(summary.outstanding))}`} />}
                </Stack>
                <Box sx={{ height: 650 }}>
                  <DataGrid
                    rows={perGuest}
                    columns={guestColumns}
                    getRowId={r => r.id}
                    density="compact"
                    slots={{ toolbar: GridToolbar }}
                    {...toolbarProps}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                      sorting: { sortModel: [{ field: 'revenue', sort: 'desc' }] }
                    }}
                    onRowClick={p => setSelectedGuestId(p?.id ?? null)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            {selectedGuest ? (
              <Stack spacing={2}>
                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1">
                        <Icon icon="mdi:receipt-text-outline" style={{ marginRight: 8 }} />
                        Billing — {safeString(selectedGuest.name)}
                      </Typography>
                      <Typography variant="caption">
                        Revenue {fCurrency(safeNumber(selectedGuest.revenue))} | Profit {fCurrency(safeNumber(selectedGuest.profit))}
                      </Typography>
                    </Stack>
                    <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                      <DataGrid
                        rows={ensureArray(selectedGuest.billing)}
                        columns={[
                          { field: 'datetime', headerName: 'Date', flex: 1, minWidth: 150, valueFormatter: p => toDateStr(p.value) },
                          { field: 'title', headerName: 'Title', flex: 1, minWidth: 120, valueFormatter: p => safeString(p.value) },
                          { field: 'category', headerName: 'Category', width: 120, valueFormatter: p => safeString(p.value) },
                          { field: 'qty', headerName: 'Qty', width: 70, valueFormatter: p => safeNumber(p.value) },
                          { field: 'amount', headerName: 'Amount', width: 120, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                          { field: 'cost', headerName: 'Cost', width: 120, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                        ]}
                        getRowId={r => r.id}
                        autoHeight
                        density="compact"
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                        {...toolbarProps}
                        pageSizeOptions={[10]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      />
                    </Box>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle1">
                        <Icon icon="mdi:credit-card-outline" style={{ marginRight: 8 }} />
                        Payments — {safeString(selectedGuest.name)}
                      </Typography>
                      <Typography variant="caption">
                        Paid {fCurrency(safeNumber(selectedGuest.payments))} | Balance {fCurrency(safeNumber(selectedGuest.balance))}
                      </Typography>
                    </Stack>
                    <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
                      <DataGrid
                        rows={ensureArray(selectedGuest.paymentsList)}
                        columns={[
                          { field: 'dateTime', headerName: 'Date', flex: 1, minWidth: 150, valueFormatter: p => toDateStr(p.value) },
                          { field: 'title', headerName: 'Title', flex: 1, minWidth: 120, valueFormatter: p => safeString(p.value) },
                          { field: 'paymentType', headerName: 'Type', width: 100, valueFormatter: p => safeString(p.value) },
                          { field: 'amount', headerName: 'Amount', width: 120, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                        ]}
                        getRowId={r => r.id}
                        autoHeight
                        density="compact"
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                        {...toolbarProps}
                        pageSizeOptions={[10]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            ) : (
              <Box sx={{ height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, bgcolor: 'background.paper' }}>
                <Typography variant="body2">Select a guest to view billing and payments</Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1">Reports & Data Views</Typography>
                </Stack>
                <Tabs value={safeTab} onChange={(_, v) => setReportTab(Number.isInteger(v) ? v : 0)} sx={{ mb: 1 }} variant="scrollable" allowScrollButtonsMobile>
                  <Tab label="By Category" />
                  <Tab label="Payment Types" />
                  <Tab label="Outstanding" />
                  <Tab label="Daily Summary" />
                  <Tab label="Top Items" />
                </Tabs>

                {safeTab === 0 && (
                  <Box sx={{ height: 420 }}>
                    <DataGrid
                      rows={categoryRows}
                      columns={[
                        { field: 'category', headerName: 'Category', flex: 1, minWidth: 160, valueFormatter: p => safeString(p.value) },
                        { field: 'revenue', headerName: 'Revenue', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                      ]}
                      getRowId={r => r.id}
                      density="compact"
                      slots={{ toolbar: GridToolbar }}
                      {...toolbarProps}
                      pageSizeOptions={[10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } }, sorting: { sortModel: [{ field: 'revenue', sort: 'desc' }] } }}
                    />
                  </Box>
                )}

                {safeTab === 1 && (
                  <Box sx={{ height: 420 }}>
                    <DataGrid
                      rows={paymentTypeRows}
                      columns={[
                        { field: 'type', headerName: 'Payment Type', flex: 1, minWidth: 160, valueFormatter: p => safeString(p.value) },
                        { field: 'amount', headerName: 'Amount', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                      ]}
                      getRowId={r => r.id}
                      density="compact"
                      slots={{ toolbar: GridToolbar }}
                      {...toolbarProps}
                      pageSizeOptions={[10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } }, sorting: { sortModel: [{ field: 'amount', sort: 'desc' }] } }}
                    />
                  </Box>
                )}

                {safeTab === 2 && (
                  <Box sx={{ height: 420 }}>
                    <DataGrid
                      rows={outstandingRows}
                      columns={[
                        { field: 'name', headerName: 'Guest', flex: 1, minWidth: 160, valueFormatter: p => safeString(p.value) },
                        { field: 'revenue', headerName: 'Revenue', width: 140, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                        { field: 'payments', headerName: 'Payments', width: 140, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                        { field: 'balance', headerName: 'Outstanding', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                      ]}
                      getRowId={r => r.id}
                      density="compact"
                      slots={{ toolbar: GridToolbar }}
                      {...toolbarProps}
                      pageSizeOptions={[10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } }, sorting: { sortModel: [{ field: 'balance', sort: 'desc' }] } }}
                    />
                  </Box>
                )}

                {safeTab === 3 && (
                  <Box sx={{ height: 420 }}>
                    <DataGrid
                      rows={dailyRows}
                      columns={[
                        { field: 'date', headerName: 'Date', width: 140, valueFormatter: p => safeString(p.value) },
                        { field: 'items', headerName: 'Items', width: 100, valueFormatter: p => safeNumber(p.value) },
                        { field: 'revenue', headerName: 'Revenue', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                        { field: 'cost', headerName: 'Cost', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                        { field: 'profit', headerName: 'Profit', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                      ]}
                      getRowId={r => r.id}
                      density="compact"
                      slots={{ toolbar: GridToolbar }}
                      {...toolbarProps}
                      pageSizeOptions={[10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } }, sorting: { sortModel: [{ field: 'date', sort: 'desc' }] } }}
                    />
                  </Box>
                )}

                {safeTab === 4 && (
                  <Box sx={{ height: 420 }}>
                    <DataGrid
                      rows={productRows}
                      columns={[
                        { field: 'title', headerName: 'Item', flex: 1, minWidth: 180, valueFormatter: p => safeString(p.value) },
                        { field: 'qty', headerName: 'Qty', width: 100, valueFormatter: p => safeNumber(p.value) },
                        { field: 'count', headerName: 'Lines', width: 90, valueFormatter: p => safeNumber(p.value) },
                        { field: 'revenue', headerName: 'Revenue', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                        { field: 'cost', headerName: 'Cost', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) },
                        { field: 'profit', headerName: 'Profit', width: 160, valueFormatter: p => fCurrency(safeNumber(p.value)) }
                      ]}
                      getRowId={r => r.id}
                      density="compact"
                      slots={{ toolbar: GridToolbar }}
                      {...toolbarProps}
                      pageSizeOptions={[10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } }, sorting: { sortModel: [{ field: 'revenue', sort: 'desc' }] } }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default FinanceDashboard;
